import { type MouseEvent, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { css } from '@/styled-system/css';
import { Box, Paragraph, Table, Tag } from '@zillow/constellation-10';
import { camelCase } from 'es-toolkit';
import StatusLabel from '../StatusLabel';
import { AppContext } from '../../contexts/AppContextProvider';
import { LeadManagementContext } from '../../contexts/LeadManagementContextProvider';
import useLeadManagementTableQuery, {
  getLeadManagementTableQueryKey,
  useCachedLeadByConversationId,
  useInvalidateLeadManagementTable,
} from '../../hooks/useLeadManagementTableQuery';
import useRealtimeUpdates, {
  LIST_MUTATING_EVENT_TYPES,
  useGuardedRealtimeUpdate,
} from '../../hooks/useRealtimeUpdates';
import { useInvalidateMessageCounts } from '../../hooks/useInvalidateMessageCounts';
import useModal from '../../hooks/useModal';
import useOpenRenterDetails from '../../hooks/useOpenRenterDetails';
import shouldIgnoreRowClick, { type PointerDownPosition } from '../../utils/shouldIgnoreRowClick';
import { ModalType } from '../../../src/types/ModalType';
import LatestContactMessage from './components/LatestContactMessage';
import ScreeningCriteriaTooltip from './components/ScreeningCriteriaTooltip';
import LeadNameButton from './components/LeadNameButton';
import MoreActionsMenu from './components/MoreActionsMenu';
import getRenterDisplayName, { isNamelessPhoneLead } from '../../utils/getRenterDisplayName';
import NextStepTextButton from './components/NextStepTextButton';
import StarIconButton from '../StarIconButton';
import LeadManagementModals from '../LeadManagementModals';
import LeadManagementTableSkeleton from './Skeleton';
import { emitOpenLeadProfileCardEvent } from '../../constants/clickstreamEvents/renterDetailsModal';
import { useLoadMoreErrorToast } from '../../hooks/useLoadMoreErrorToast';
import { getLeadRestrictions } from '../../hooks/useLeadRestrictions';
import useStreetEasyLeadManagementEnabled from '../../hooks/useStreetEasyLeadManagementEnabled';
import useIsLeadSourceVisible from '../../hooks/useIsLeadSourceVisible';
import { IsVisible } from '@zg-rentals/react-hooks';
import { useAuth } from '../../AuthProvider';
import FolderStatusIcon from './components/FolderStatusIcon';
import ListingAddress from './components/ListingAddress';

const TABLE_COLUMNS = ['Name', 'Listing', 'Screening', 'Status', 'Latest contact', 'Next step'];
const TABLE_COLUMNS_WITH_SOURCE = ['Name', 'Listing', 'Source', 'Screening', 'Status', 'Latest contact', 'Next step'];

/** Shared with the skeleton so its header and cell count match the rendered table. */
export const getTableColumns = (showSourceColumn: boolean) =>
  showSourceColumn ? TABLE_COLUMNS_WITH_SOURCE : TABLE_COLUMNS;

const tableRowStyles = css.raw({
  '& [data-testid="more-actions-menu-popper"]': {
    verticalAlign: 'middle',
  },

  '&[data-isActiveRow=true]': {
    '& #lead-name': {
      textDecoration: 'underline !important',
    },
  },

  '&:last-child td, &:last-child th': {
    borderBlockEndWidth: 0,
  },
});

export default function LeadManagementTable() {
  const { isPostingPathAdmin } = useContext(AppContext);
  const {
    clientTimeZone,
    conversationId: conversationIdFromUrl,
    conversationIdToFilterBy,
    folder,
    isArchivedFolder,
    isReportedFolder,
    listingAlias,
    listingAliases,
    messagingConversationId,
    minCriteriaMatches,
    readRepliedStatus,
    renterProgressStatus,
    source,
  } = useContext(LeadManagementContext);
  const { setActiveModal } = useModal();
  const { canOpenRenterDetails, openRenterDetails } = useOpenRenterDetails();
  const rowPointerDownRef = useRef<PointerDownPosition | null>(null);
  const { user } = useAuth();
  const isStreetEasyLeadManagementEnabled = useStreetEasyLeadManagementEnabled();
  const showSourceColumn = useIsLeadSourceVisible();
  const [activeRowId, setActiveRowId] = useState('');
  const hasConversationIdFromUrl = !!conversationIdFromUrl;
  const openedLead = useCachedLeadByConversationId(conversationIdFromUrl || '');

  const queryParams = {
    clientTimeZone,
    conversationId: conversationIdToFilterBy,
    listingAliases,
    folder,
    minCriteriaMatches,
    readRepliedStatus,
    renterProgressStatus,
    source,
  };
  const { leads, hasNextPage, isLoading, isError, error, isFetchingNextPage, isFetchNextPageError, fetchNextPage } =
    useLeadManagementTableQuery(queryParams);

  const invalidateLeadManagementTable = useInvalidateLeadManagementTable();
  const invalidateMessageCounts = useInvalidateMessageCounts();
  const handleRealtimeUpdate = useGuardedRealtimeUpdate(
    error,
    useCallback(() => {
      invalidateLeadManagementTable();
      invalidateMessageCounts();
    }, [invalidateLeadManagementTable, invalidateMessageCounts]),
  );

  useRealtimeUpdates({
    activeConversationId: messagingConversationId,
    clientTimeZone,
    onRealtimeUpdate: handleRealtimeUpdate,
    // subscribeToMessages delivers every frame from every subscribed topic, and one message fans
    // out into several event types — restrict to list-level types so a single received message
    // invalidates the table once, not once per frame. The open conversation's `newMessage` is
    // owned by useMessageListRealtimeUpdates.
    refetchOnEventTypes: LIST_MUTATING_EVENT_TYPES,
  });

  useLoadMoreErrorToast({
    isFetchNextPageError,
    isFetchingNextPage,
    hasNextPage,
    // Mirrors what useLeadManagementTableQuery keys on, so this changes exactly when the query
    // switches to a different cache entry (folder/filter change).
    queryKey: JSON.stringify(getLeadManagementTableQueryKey(queryParams)),
    onReset: invalidateLeadManagementTable,
  });

  // Open the renter details modal any time we detect a conversationId param in the URL
  // and highlight the table row that corresponds to the conversationId
  useEffect(() => {
    if (conversationIdFromUrl && !isArchivedFolder && !isReportedFolder) {
      setActiveModal(ModalType.RenterDetailsModal);
      setActiveRowId(conversationIdFromUrl);
    } else if (conversationIdToFilterBy) {
      setActiveRowId(conversationIdToFilterBy);
    } else {
      setActiveRowId('');
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [conversationIdFromUrl, conversationIdToFilterBy]);

  // Trigger a clickstream event only when the renter details modal is opened. Wait for the opened
  // lead to resolve (null on a cold deep link until the leads query lands) so the event carries
  // the row alias instead of the context fallback ('' unless exactly one listing is selected); the ref keeps it
  // once per open.
  const hasEmittedOpenLeadProfileRef = useRef(false);
  useEffect(() => {
    if (!hasConversationIdFromUrl) {
      hasEmittedOpenLeadProfileRef.current = false;
      return;
    }
    if (hasEmittedOpenLeadProfileRef.current || !openedLead) return;
    hasEmittedOpenLeadProfileRef.current = true;
    emitOpenLeadProfileCardEvent(
      user,
      { listingAlias: openedLead.listingDetails?.listingAlias ?? listingAlias },
      { leadId: openedLead.renterInfo?.leadId },
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [hasConversationIdFromUrl, openedLead]);

  const isLoadingLeadsData = isLoading;

  if (isLoadingLeadsData) {
    return <LeadManagementTableSkeleton showSourceColumn={showSourceColumn} />;
  }

  const handleFetchMoreLeads = (isVisible: boolean) => {
    if (!isVisible || isFetchingNextPage) return;
    fetchNextPage();
  };

  // Mouse-only shortcut: the name button stays the keyboard and screen-reader path to the drawer.
  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>, conversationId: string) => {
    if (!canOpenRenterDetails || shouldIgnoreRowClick(event, rowPointerDownRef.current)) return;
    openRenterDetails(conversationId);
  };

  return (
    <>
      {activeRowId && <LeadManagementModals activeRowId={activeRowId} setActiveRowId={setActiveRowId} />}

      <Table.Responsive>
        <Table.Root
          aria-label="Leads"
          appearance="horizontal"
          size="lg"
          css={{
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.muted-alt',
            padding: 'default',
            borderRadius: 'obj.default',
          }}
        >
          <Table.Header
            sticky
            css={{
              top: isPostingPathAdmin ? '98px' : '60px',
              zIndex: 1,
            }}
          >
            <Table.Row>
              {getTableColumns(showSourceColumn).map((name) => {
                const colId = camelCase(name);
                return (
                  <Table.HeaderCell
                    key={colId}
                    css={{
                      whiteSpace: 'nowrap',
                      paddingLeft: name === 'Name' ? '55px' : '', // aligns header with star + name cell
                    }}
                  >
                    {name}
                  </Table.HeaderCell>
                );
              })}
            </Table.Row>
          </Table.Header>

          <Table.Body>
            <>
              {leads.map(
                ({
                  conversationId,
                  disabledFeatures,
                  latestContact,
                  listingDetails,
                  renterInfo,
                  screeningCriteria,
                  source,
                  status,
                  statusLabel,
                }) => {
                  const { leadId, renterName } = renterInfo;
                  const { isNew, isPhoneLead } = latestContact;
                  const { isConversationBlockedByZillow, isFavorite } = status;
                  // Plain util (not the hook) — this runs per row inside the map.
                  const { canRequestRenterProfile, canViewScreeningCriteria } = getLeadRestrictions(
                    isStreetEasyLeadManagementEnabled ? { disabledFeatures } : null,
                  );
                  const renterNameDisplay = getRenterDisplayName(renterName, isPhoneLead, '');
                  const isPhoneLeadLabel = isNamelessPhoneLead(renterName, isPhoneLead);
                  const isActiveRow = conversationId === activeRowId;
                  const statusLabelExists = !!statusLabel;

                  return (
                    <Table.Row
                      interactive={canOpenRenterDetails}
                      key={conversationId}
                      data-testid="lead-row"
                      data-conversation-id={conversationId}
                      data-isActiveRow={isActiveRow}
                      css={tableRowStyles}
                      aria-selected={isActiveRow}
                      onClick={(event) => handleRowClick(event, conversationId)}
                      onPointerDown={({ clientX, clientY }) => {
                        rowPointerDownRef.current = { clientX, clientY };
                      }}
                    >
                      <Table.HeaderCell
                        css={{
                          verticalAlign: 'middle',
                          paddingLeft: 0,
                          paddingRight: 'tighter',
                        }}
                      >
                        <Box css={{ display: 'flex', alignItems: 'center' }}>
                          <Box css={{ marginRight: 'tighter' }}>
                            <StarIconButton
                              conversationId={conversationId}
                              isConversationBlockedByZillow={isConversationBlockedByZillow}
                              isFavorite={isFavorite}
                            />
                          </Box>
                          <LeadNameButton
                            conversationId={conversationId}
                            isPhoneLeadLabel={isPhoneLeadLabel}
                            leadName={renterNameDisplay}
                          />
                          {isNew && (
                            <Tag
                              tone="aqua"
                              size="sm"
                              css={{
                                marginLeft: 'tighter',
                                textTransform: 'uppercase',
                              }}
                            >
                              New
                            </Tag>
                          )}
                          <FolderStatusIcon />
                        </Box>
                      </Table.HeaderCell>

                      <Table.Cell
                        css={{
                          verticalAlign: 'middle',
                          // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 130px` falls between `2600` (104px) and `3600` (144px) on the numeric sizes scale
                          maxWidth: '130px',
                        }}
                      >
                        <ListingAddress listingDetails={listingDetails} />
                      </Table.Cell>

                      {showSourceColumn && (
                        // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 130px` falls between `2600` (104px) and `3600` (144px) on the numeric sizes scale
                        <Table.Cell css={{ verticalAlign: 'middle', maxWidth: '130px' }}>
                          <Paragraph textStyle="body-sm">{source.displayName}</Paragraph>
                        </Table.Cell>
                      )}

                      <Table.Cell css={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        {canViewScreeningCriteria && (
                          <ScreeningCriteriaTooltip
                            canRequestRenterProfile={canRequestRenterProfile}
                            conversationId={conversationId}
                            isPhoneLead={isPhoneLead}
                            leadId={leadId}
                            screeningCriteria={screeningCriteria}
                            setActiveRowId={setActiveRowId}
                            shouldDisplayRequestedText={true}
                          />
                        )}
                      </Table.Cell>

                      <Table.Cell
                        css={{
                          verticalAlign: 'middle',
                          whiteSpace: 'nowrap',
                          // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 180px` falls between `4000` (160px) and `5000` (200px) on the numeric sizes scale
                          maxWidth: '180px',
                        }}
                      >
                        {statusLabelExists ? (
                          <StatusLabel linkId={statusLabel.linkId} text={statusLabel.text} />
                        ) : (
                          <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
                            Not available
                          </Paragraph>
                        )}
                      </Table.Cell>

                      <Table.Cell
                        css={{
                          maxWidth: '5000',
                          verticalAlign: 'middle',
                        }}
                      >
                        <LatestContactMessage
                          conversationId={conversationId}
                          isConversationBlockedByZillow={isConversationBlockedByZillow}
                          latestContactDetails={latestContact}
                          leadId={leadId}
                          listingAlias={listingDetails.listingAlias}
                        />
                      </Table.Cell>

                      <Table.Cell
                        css={{
                          verticalAlign: 'middle',
                          whiteSpace: 'nowrap',
                          paddingRight: 0,
                        }}
                      >
                        <Box
                          css={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <NextStepTextButton conversationId={conversationId} setActiveRowId={setActiveRowId} />
                          <Box css={{ marginLeft: 'auto' }}>
                            <MoreActionsMenu
                              conversationId={conversationId}
                              iconColor="brand"
                              setActiveRowId={setActiveRowId}
                            />
                          </Box>
                        </Box>
                      </Table.Cell>
                    </Table.Row>
                  );
                },
              )}
            </>
          </Table.Body>
        </Table.Root>
      </Table.Responsive>

      {hasNextPage && !isError && !isFetchNextPageError && (
        <IsVisible onChange={handleFetchMoreLeads}>
          <LeadManagementTableSkeleton numberOfRows={2} showHeader={false} showSourceColumn={showSourceColumn} />
        </IsVisible>
      )}

      <Paragraph
        css={{
          textAlign: 'center',
          paddingTop: 'loose',
          paddingBottom: 'loose',
        }}
      >
        This table shows leads you’ve received in the past 90 days.
      </Paragraph>
    </>
  );
}
